/**
 * 极简的 HTTP wrapper — 自动注入 Authorization Bearer + JSON 解析,
 * 不引入 axios 减少 bundle 体积(企微 H5 流量敏感)。
 */
import { notifyPermissionDenied } from '../composables/useToast';
import { permissionDeniedOf, serverMessageOf, wxworkNotLinkedBody } from './staffErrors';

const STORAGE_KEY = 'cat.internal.auth';

/** OAuth2 回调页路径 (也是非企微环境下的开发者登录页) */
export const OAUTH_CALLBACK_PATH = '/auth/oauth2-callback';

/**
 * 业务上"401 是预期可能"的端点 — 不应触发清 token + 重新登录。
 * 这些端点的调用方负责自己处理 401:
 *   - /api/internal/auth/wxwork-oauth2 : OAuth2 换 JWT,失败时(binding 没建)
 *     UI 应停下提示,让用户先 SaveBinding,而不是清 token + 又跳 OAuth2 死循环
 *   - /api/internal/wxwork-config : 公开端点,理论上不会 401,真返了也别死循环
 */
const ENDPOINTS_BYPASS_401_REDIRECT = [
  '/api/internal/auth/wxwork-oauth2',
  '/api/internal/wxwork-config',
  // wxwork/greeters 是顶栏 AppShell 调的背景请求 — 即使 401 也别清 token + redirect,
  // 否则在 OAuth callback page 初次渲染(layout 还没解析)的瞬间, AppShell 短暂 mount
  // 调一次 listGreeters 没 token → 401 → 跳转冲断了 OAuth flow,
  // 用户永远停在 splash. AppShell 自己已 catch 401 当做"未拿到状态"处理.
  '/api/internal/wxwork/greeters',
];

/** 接口返回非 2xx 时抛出的错误; message 保持 "HTTP <status>: <正文>" 格式, 供 friendlyError 解析 */
export class HttpError extends Error {
  /** HTTP 状态码 */
  readonly status: number;
  /** 响应正文原文 */
  readonly bodyText: string;

  constructor(status: number, bodyText: string, statusText: string) {
    super(`HTTP ${status}: ${bodyText || statusText}`);
    this.name = 'HttpError';
    this.status = status;
    this.bodyText = bodyText;
  }

  /** 把正文按 JSON 解析并转成 camelCase; 不是 JSON 时返回 null */
  json<T>(): T | null {
    if (!this.bodyText) return null;
    try {
      return pascalToCamel(JSON.parse(this.bodyText)) as T;
    } catch {
      return null;
    }
  }
}

/** 企业微信成员还没关联 Cat 账号 (OAuth2 换令牌 403 { code: 'wxworkNotLinked', wxworkUserId, name }); 不是时返回 null */
export function wxworkNotLinkedOf(e: unknown): { wxworkUserId: string; name: string } | null {
  return e instanceof HttpError ? wxworkNotLinkedBody(e.status, e.bodyText) : null;
}

/** 接口错误里服务器给的说明 (message / detail / title); 没有时为 null */
export function serverMessage(e: unknown): string | null {
  return e instanceof HttpError ? serverMessageOf(e.bodyText) : null;
}

export function readToken(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return '';
    return JSON.parse(raw).token || '';
  } catch {
    return '';
  }
}

/**
 * 把"登录后回到哪里"限制为站内路径: 必须以单个 / 开头, 不能是 OAuth2 回调页本身; 否则回首页。
 * 防止 ?return= 被用来跳到站外, 也防止回调页回到回调页循环。
 */
export function safeReturnPath(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length === 0) return '/';
  // 控制字符 (含换行 / 制表符) 与反斜杠会被 URL 解析吞掉或当成 /, 可能拼出 //站外地址, 一律拒绝
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return '/';
  }
  if (url.origin !== window.location.origin) return '/';
  const path = url.pathname.replace(/\/+$/, '') || '/';
  if (path === OAUTH_CALLBACK_PATH || path.startsWith(`${OAUTH_CALLBACK_PATH}/`)) return '/';
  return `${url.pathname}${url.search}${url.hash}`;
}

/* ── 401 重新登录 ─────────────────────────────── */

/** 自动重新登录的时间记录 (sessionStorage, 跨页面跳转保留) */
const REAUTH_LOG_KEY = 'cat.reauth-log';
/** 统计窗口 */
const REAUTH_WINDOW_MS = 60_000;
/** 窗口内最多自动重新登录的次数, 再失败就停在错误页 */
const REAUTH_MAX_IN_WINDOW = 2;

/** 重新登录的执行方式: 由路由模块注册 (企微内走 OAuth2, 其它环境去登录页) */
export type ReauthHandler = (returnTo: string) => void;

let reauthHandler: ReauthHandler | null = null;
/** 本页面已发起重新登录 (单次飞行: 并发的多个 401 只处理第一个) */
let reauthStarted = false;

/** 注册 401 时的重新登录方式 */
export function setReauthHandler(handler: ReauthHandler) {
  reauthHandler = handler;
}

/** 清除自动重新登录的失败记录 (用户在错误页手动重试时调用) */
export function resetReauthLog() {
  try {
    sessionStorage.removeItem(REAUTH_LOG_KEY);
  } catch {
    // sessionStorage 不可用时忽略
  }
}

function readReauthLog(now: number): number[] {
  try {
    const raw = sessionStorage.getItem(REAUTH_LOG_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list)
      ? list.filter((t): t is number => typeof t === 'number' && now - t < REAUTH_WINDOW_MS)
      : [];
  } catch {
    return [];
  }
}

function writeReauthLog(list: number[]) {
  try {
    sessionStorage.setItem(REAUTH_LOG_KEY, JSON.stringify(list));
  } catch {
    // sessionStorage 不可用时不限次数, 仍靠单次飞行防并发
  }
}

/** 当前页面的站内路径 (含查询串和锚点) */
function currentInSitePath(): string {
  return safeReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
}

/**
 * 处理 401: 登录已失效时保留当前页面重新登录。
 *   - 豁免端点不处理 (调用方自行展示错误)
 *   - 已经在重新登录: 不重复发起
 *   - 出错请求用的 token 已不是当前 token (别处已换了新登录): 不清除, 只把错误抛给调用方
 *   - 一分钟内已自动重新登录过 REAUTH_MAX_IN_WINDOW 次: 停在登录页的错误状态, 不再自动跳转
 */
function handleUnauthorized(path: string, usedToken: string) {
  if (ENDPOINTS_BYPASS_401_REDIRECT.some((p) => path.startsWith(p))) return;
  if (reauthStarted) return;
  if (usedToken !== readToken()) return;

  reauthStarted = true;
  localStorage.removeItem(STORAGE_KEY);
  const returnTo = currentInSitePath();

  const now = Date.now();
  const log = readReauthLog(now);
  if (log.length >= REAUTH_MAX_IN_WINDOW) {
    window.location.replace(
      `${OAUTH_CALLBACK_PATH}?reauth=failed&return=${encodeURIComponent(returnTo)}`);
    return;
  }
  writeReauthLog([...log, now]);

  if (reauthHandler) {
    reauthHandler(returnTo);
  } else {
    window.location.replace(`${OAUTH_CALLBACK_PATH}?return=${encodeURIComponent(returnTo)}`);
  }
}

async function failResponse(resp: Response, path: string, usedToken: string): Promise<never> {
  if (resp.status === 401) handleUnauthorized(path, usedToken);
  const text = await resp.text();
  // 403 permissionDenied: 账号没勾对应权限, 提示缺哪项、找负责人勾选 (与页面的错误提示同一句, 提示条只留一条)
  const denied = permissionDeniedOf(resp.status, text);
  if (denied !== null) notifyPermissionDenied(denied);
  throw new HttpError(resp.status, text, resp.statusText);
}

export async function http<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(path, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) return failResponse(resp, path, token);
  if (resp.status === 204) return null as T;
  const ct = resp.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    const raw = await resp.json();
    // 后端 Program.cs 全局 PropertyNamingPolicy=null,API 返 PascalCase("Token", "ExpiresAt"...)。
    // 自动转 camelCase 让前端 TS 接口保持惯用风格,且消除"前端 result.token 永远 undefined"
    // 这种隐形 bug(踩过一次,吃尽苦头)。深拷贝转换,保留 null/array。
    return pascalToCamel(raw) as T;
  }
  return await resp.text() as unknown as T;
}

/**
 * 递归把 PascalCase 字段名转成 camelCase。处理:
 * - object: 每个 key 首字母小写
 * - array: 元素递归
 * - primitive (string/number/boolean/null): 原样返回
 *
 * 不做特殊处理 'OAuth2' 这种连续大写情况 — 'OAuth2RedirectUrl' → 'oAuth2RedirectUrl'。
 * 跟 .NET DefaultJsonSerializerOptions.PropertyNamingPolicy = JsonNamingPolicy.CamelCase 行为一致。
 */
function pascalToCamel(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(pascalToCamel);
  if (typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const camel = k.length > 0 ? k.charAt(0).toLowerCase() + k.slice(1) : k;
    out[camel] = pascalToCamel(v);
  }
  return out;
}

export const apiGet = <T>(path: string) => http<T>('GET', path);
export const apiPost = <T>(path: string, body?: unknown) => http<T>('POST', path, body);
export const apiPut = <T>(path: string, body?: unknown) => http<T>('PUT', path, body);
export const apiDelete = <T>(path: string) => http<T>('DELETE', path);

/**
 * 拿二进制响应 (如图片) — 复用 Bearer token + 401 处理, 返回 Blob 供 URL.createObjectURL 展示/下载。
 */
export async function apiGetBlob(path: string): Promise<Blob> {
  const token = readToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(path, { headers });
  if (!resp.ok) return failResponse(resp, path, token);
  return await resp.blob();
}

/** 下载进度回调: loaded 为已收到的字节数, total 为总字节数 (服务器没给 Content-Length 时为 null) */
export type DownloadProgress = (loaded: number, total: number | null) => void;

/**
 * 拿二进制响应并逐块报告进度 — 复用 Bearer token + 401 处理; 浏览器不支持流式读取时整体读取后报告一次。
 */
export async function apiGetBlobWithProgress(path: string, onProgress?: DownloadProgress): Promise<Blob> {
  const token = readToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(path, { headers });
  if (!resp.ok) return failResponse(resp, path, token);

  const lengthHeader = Number(resp.headers.get('content-length'));
  const total = Number.isFinite(lengthHeader) && lengthHeader > 0 ? lengthHeader : null;
  const type = resp.headers.get('content-type') || 'application/octet-stream';
  const reader = resp.body?.getReader();
  if (!reader) {
    const blob = await resp.blob();
    onProgress?.(blob.size, total ?? blob.size);
    return blob;
  }

  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress?.(0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress?.(loaded, total);
  }
  return new Blob(chunks as BlobPart[], { type });
}

/**
 * multipart/form-data 上传 — 不走 JSON wrapper (FormData 不能 JSON.stringify),
 * 但复用同一套 Bearer token + 401 解析逻辑。
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // 注意: 不要手动设 Content-Type, 让浏览器带 boundary

  const resp = await fetch(path, { method: 'POST', headers, body: form });
  if (!resp.ok) return failResponse(resp, path, token);
  const ct = resp.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    return pascalToCamel(await resp.json()) as T;
  }
  return await resp.text() as unknown as T;
}
